import React, { useCallback, useState, memo } from 'react';
import { useDispatch } from 'react-redux';
import orderBy from 'lodash/orderBy';

import { User } from 'Shared/constants/types';
import { getActionItems } from 'Shared/selectors/meetingItems';
import { useDeepEqualSelector } from 'Shared/utils/hooks';
import UserDataContainer from 'Shared/components/UserDataContainer/UserDataContainer';
import CompletedItem from 'Dashboard/components/CompletedItem/CompletedItem';
import WpButton from 'Wpds/components/button/WpButton';
import Spinner from 'Shared/components/Spinner';
import { fetchAdditionalActionitemsAction } from 'Shared/actions/meetingItems';
import { ActionItemsContext } from 'Shared/api/types';

const LIMIT_FETCH_FOR_COMPLETED_ITEMS = 10;

type Props = {
  user: User;
  countItems: number;
};

const TeamUserCompletedItems = ({ user, countItems }: Props) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [countItemsInDB, setCountItemsInDB] = useState(countItems);
  const completedActionItemsUuids = useDeepEqualSelector(state =>
    orderBy(
      getActionItems(state).filter(
        ({ assignmentPersonId, completed }) => assignmentPersonId === user.personId && completed
      ),
      ['completedAt'],
      ['desc']
    ).map(({ uuid }) => uuid)
  );
  const [offset, setOffset] = useState(completedActionItemsUuids?.length);

  const handleViewMoreClick = useCallback(async () => {
    setIsLoading(true);
    const { countItemsByUserId } = await dispatch(
      fetchAdditionalActionitemsAction({
        context: ActionItemsContext.assignedTo,
        status: 'completed',
        limit: LIMIT_FETCH_FOR_COMPLETED_ITEMS,
        offset,
        assigneeId: user.id,
      })
    );
    setCountItemsInDB(countItemsByUserId[user.id]);
    setOffset(offset + LIMIT_FETCH_FOR_COMPLETED_ITEMS);
    setIsLoading(false);
  }, [dispatch, offset, user.id]);

  if (!completedActionItemsUuids?.length) return null;

  const showViewMoreButton =
    (countItemsInDB && countItemsInDB > completedActionItemsUuids.length) ||
    (!countItemsInDB && completedActionItemsUuids.length >= 3);

  return (
    <UserDataContainer user={user}>
      <div className="mt-3 pb-2">
        {completedActionItemsUuids.map(uuid => (
          <CompletedItem key={uuid} uuid={uuid} withWorkspaceSection />
        ))}
        {isLoading && <Spinner activeIf />}
        {showViewMoreButton && !isLoading && (
          <WpButton color="primary" variant="tertiary" size="sm" onClick={handleViewMoreClick}>
            View more
          </WpButton>
        )}
      </div>
    </UserDataContainer>
  );
};

export default memo(TeamUserCompletedItems);
